import { memo } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useTranslations } from '@/core/translation'
import { useDeviceType } from '@/utils'
import { BottomDrawer, PriceRange, PriceRangeFilterDropdown } from '@/components/Surface'
import { FilterChip } from '@/components/Utils'
import { usePriceRangeFilterResponsiveController } from './PriceRangeFilterResponsive.controller'
import { PriceRangeFilterResponsiveProps } from './PriceRangeFilterResponsive.types'

function PriceRangeFilterResponsive({
  onClear,
  buttonProps,
  popperProps,
  onChange,
  hide,
  ...props
}: PriceRangeFilterResponsiveProps) {
  const { isMobile } = useDeviceType()

  const hasValue =
    (props.value.min !== props.min || props.value.max !== props.max) &&
    props.value.min !== 0 &&
    props.value.max !== 0

  const onChangeDebounced = useDebouncedCallback(onChange, 150)

  if (hide || props.min === props.max) return null

  if (isMobile)
    return (
      <PriceRangeFilterMobile
        onClear={onClear}
        buttonProps={buttonProps}
        onChange={onChange}
        {...props}
      />
    )

  return (
    <PriceRangeFilterDropdown
      isSelected={hasValue}
      buttonProps={{ onClear, ...buttonProps }}
      onChange={onChangeDebounced}
      popperProps={{
        placement: 'bottom-start',
        ...popperProps,
      }}
      {...props}
    />
  )
}

function PriceRangeFilterMobile(props: PriceRangeFilterResponsiveProps) {
  const { onClear, min, max } = props
  const t = useTranslations('components')

  const {
    displayLabel,
    hasValue,
    isDrawerOpen,
    setIsDrawerOpen,
    closeDrawer,
    onApply,
    intermediateValue,
    setIntermediateValue,
  } = usePriceRangeFilterResponsiveController(props)

  return (
    <>
      <FilterChip
        label={displayLabel}
        isActive={hasValue}
        onClick={() => setIsDrawerOpen(true)}
        onClear={onClear}
      />

      <BottomDrawer
        open={isDrawerOpen}
        onOpen={() => {}}
        onClose={closeDrawer}
        heading={t('filters.priceRange')}
        actionButtonProps={{
          primaryActionText: t('filters.apply'),
          onPrimaryActionClick: onApply,
          secondaryActionText: t('filters.clear'),
          onSecondaryActionClick: () => {
            setIntermediateValue({ min, max })
          },
        }}>
        <PriceRange
          showTitle={false}
          min={min}
          max={max}
          value={intermediateValue}
          onChange={setIntermediateValue}
        />
      </BottomDrawer>
    </>
  )
}

export default memo(PriceRangeFilterResponsive)
